"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductSortDropdown, ProductsFilter } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
function ProductGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="163761" className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] w-full animate-pulse rounded-none bg-muted/20" />
            <div className="h-5 w-2/3 animate-pulse rounded-none bg-muted/20" />
            <div className="h-4 w-1/3 animate-pulse rounded-none bg-muted/20" />
            <div className="mt-auto h-10 w-full animate-pulse rounded-none bg-muted/20" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">Failed to reveal offerings.</p>
        <Button variant="outline" onClick={refetch} className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Re-Invoke
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-xl font-default font-medium text-foreground tracking-tight">No offerings found</p>
        <p className="mt-2 text-muted-foreground font-default">
          Adjust your frequency to find the ritual tools you seek.
        </p>
      </div>;
  }
  return <div className="flex flex-col space-y-10">
      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => <div key={product.id} data-index={i} className="group relative flex flex-col">
            <Link to={product.link} className="mb-4 block aspect-[4/5] overflow-hidden rounded-none bg-accent">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover object-center transition-transform duration-1000 ease-out group-hover:scale-110 grayscale group-hover:grayscale-0" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  No Vision
                </div>}
            </Link>
            <div className="flex flex-1 flex-col">
              <h3 className="text-lg font-default font-semibold text-foreground tracking-tight">
                <Link to={product.link} className="hover:text-primary transition-colors duration-300">
                  {product.title}
                </Link>
              </h3>
              {product.price !== undefined && <p className="mt-1 text-sm font-default text-primary tracking-widest">
                  ${product.price.toFixed(2)}
                </p>}
              <div className="mt-4 mt-auto pt-4">
                <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-none font-default font-bold uppercase tracking-widest hover:bg-primary/90 transition-all duration-300 shadow-[0_0_15px_rgba(255,191,61,0.2)]" text="Secure Ritual" showQuantity={false} />
              </div>
            </div>
          </div>)}
      </div>

      {totalPages > 1 && <div className="mt-12 flex items-center justify-center gap-4 border-t border-border pt-8">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="w-24 rounded-none border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground disabled:opacity-20">
            Previous
          </Button>
          <span className="text-xs font-default font-medium text-muted-foreground uppercase tracking-widest">
            Phase {currentPage} of {totalPages}
          </span>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="w-24 rounded-none border-border bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground disabled:opacity-20">
            Next
          </Button>
        </div>}
    </div>;
}
export default function ShopContent() {
  const current_wp_query_params = typeof window !== "undefined" && (window as any).wvcClient ? (window as any).wvcClient.getCurrentWPQueryParams() : {};
  const wp_query = new WP_Query(current_wp_query_params);
  return <section id="shop-content" className="dark bg-background py-16 md:py-32 text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-20">
          <h1 className="font-default text-4xl font-bold tracking-tighter text-foreground md:text-7xl uppercase">
            The Ritual Store
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground font-default leading-relaxed tracking-normal">
            Equip your sanctuary with sacred tools. From high-vibration scents 
            to ritual attire, curated for the modern devotee to awaken the senses.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="flex flex-col gap-8 lg:flex-row lg:gap-16">
            {}
            <aside className="w-full shrink-0 lg:w-1/4">
              <div className="sticky top-24 rounded-none border border-border bg-card/50 backdrop-blur-xl p-8 shadow-2xl">
                <h2 className="mb-8 font-default text-xl font-bold text-foreground uppercase tracking-widest">
                  Sacred Elements
                </h2>
                <div className="font-default">
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            {}
            <div className="flex w-full flex-col space-y-8 lg:w-3/4">
              <div className="flex flex-col items-start justify-between gap-4 border-b border-border/50 pb-6 sm:flex-row sm:items-center">
                <p className="text-xs font-default uppercase tracking-widest text-muted-foreground">
                  Revealing the collection
                </p>
                <div className="w-full sm:w-auto font-default">
                  <ProductSortDropdown label="Align by" />
                </div>
              </div>
              <ProductGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>;
}